<template>
  <v-form ref="form" lazy-validation @submit.prevent>
    <FormSection>
      <BaseAlert v-if="softwareNoteData">
        <template #message>
          <BaseRichtext id="cfed-software-note" :html="softwareNoteData" />
        </template>
      </BaseAlert>

      <BaseSelect
        v-model="damageTypes"
        :class="['mb-6', $style.select]"
        :error-messages="damageTypesErrors"
        :hide-details="!damageTypesErrors.length"
        :label="$t('complaintForm.ticket.engine.damage')"
        :hint="$t('form.hint.selectAtLeastOne', { term: $t('complaintForm.ticket.engine.damage') })"
        :items="damageItems"
        multiple
        chips
        persistent-hint
        required
        v-on="getTouchListeners('damageTypes')"
      />

      <BaseTextarea
        v-model="formData.description"
        class="mb-6"
        :error-messages="descriptionErrors"
        :hide-details="!descriptionErrors.length"
        :label="$t('complaintForm.ticket.engine.description')"
        required
        v-on="getTouchListeners('formData.description')"
      />

      <BaseSelect
        v-model="errorFrequencyType"
        class="mb-6"
        :error-messages="errorFrequencyTypeErrors"
        :hide-details="!errorFrequencyTypeErrors.length"
        :label="$t('complaintForm.ticket.engine.error_rate')"
        :items="errorFrequencyItems"
        required
        v-on="getTouchListeners('errorFrequencyType')"
      />

      <BaseTextfield
        v-if="isSporadicErrorType"
        v-model="formData.distanceValue"
        :error-messages="distanceValueErrors"
        :hide-details="!distanceValueErrors.length"
        :label="$t('complaintForm.ticket.engine.route')"
        v-on="getTouchListeners('formData.distanceValue')"
      />
    </FormSection>

    <ComplaintFormStepperControls
      :is-valid="isValid"
      :has-required-fields="true"
      :prev-step="prevStep"
      :next-step="submit"
    />
  </v-form>
</template>

<script>
import { get } from 'lodash-es'
import { mapMutations, mapGetters } from 'vuex'
import { required, numeric } from 'vuelidate/lib/validators'

import { setLinksTargetForQuery } from '@/js/helper/links-helper.js'

import StepperMixin from '@/js/mixins/stepper-mixin.js'
import FormValidationMixin from '@/js/components/Forms/mixins/form-validation-mixin.js'
import ComplaintFormMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-default-mixin.js'

import FORM_CONSTANTS from '@/js/components/Forms/ComplaintForm/_store-module/constants.js'
import FORM_MUTATION from '@/js/components/Forms/ComplaintForm/_store-module/mutation-types.js'
import FORM_GETTER from '@/js/components/Forms/ComplaintForm/_store-module/getter-types.js'

import FormSection from '@/js/components/Forms/FormSection.vue'

import ComplaintFormStepperControls from '../../ComplaintFormStepperControls.vue'

export const COMPLAINT_FORM_IMPULSE_ERROR_DESC_CONSTANTS = {
  PERMANENT_ERROR_TYPE: 'complaintForm.ticket.engine.error_rate_1',
  SPORADIC_ERROR_TYPE: 'complaintForm.ticket.engine.error_rate_2',
}

export const COMPLAINT_FORM_IMPULSE_ERROR_DAMAGE_TYPES = {
  COMPLAINT_FORM_TICKET_ENGINE_DAMAGE_TYPE1: 'complaintForm.ticket.engine.damage_type1',
  COMPLAINT_FORM_TICKET_ENGINE_DAMAGE_TYPE2: 'complaintForm.ticket.engine.damage_type2',
  COMPLAINT_FORM_TICKET_ENGINE_DAMAGE_TYPE3: 'complaintForm.ticket.engine.damage_type3',
  COMPLAINT_FORM_TICKET_ENGINE_DAMAGE_TYPE4: 'complaintForm.ticket.engine.damage_type4',
  COMPLAINT_FORM_TICKET_ENGINE_DAMAGE_TYPE5: 'complaintForm.ticket.engine.damage_type5',
  COMPLAINT_FORM_TICKET_ENGINE_DAMAGE_TYPE6: 'complaintForm.ticket.engine.damage_type6',
}

export default {
  name: 'ComplaintFormImpulseErrorDesc',
  components: {
    FormSection,
    ComplaintFormStepperControls,
  },
  mixins: [FormValidationMixin, ComplaintFormMixin],
  props: {
    ...StepperMixin.props,
  },
  data() {
    return {
      formData: {
        damage: null,
        description: null,
        errorFrequency: null,
        distanceValue: null,
        ctdiDescriptions: {
          [FORM_CONSTANTS.CTDI_DESCRIPTIONS_KEYS.ERROR_FREQUENCY]: undefined,
          [FORM_CONSTANTS.CTDI_DESCRIPTIONS_KEYS.DAMAGE]: undefined,
        },
      },
      errorFrequencyType: null,
      damageTypes: null,
    }
  },
  validations: {
    damageTypes: {
      required,
    },
    errorFrequencyType: {
      required,
    },
    formData: {
      description: {
        required,
      },
      distanceValue: {
        numeric,
      },
    },
  },
  computed: {
    /** Getters */
    ...mapGetters(FORM_CONSTANTS.STORE_ID, {
      steps: FORM_GETTER.STEPS,
      fullFormData: FORM_GETTER.FORM_DATA,
      ctdiDescriptions: FORM_GETTER.CTDI_DESCRIPTIONS,
    }),

    /** Store data */
    softwareNoteData() {
      return this.isContinentalType
        ? this.$t('complaintForm.ticket.engine.continental_software_note')
        : get(this.steps, FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.STEPS.ERROR_INFOS, {})?.software_note
    },

    /** Computed helper */
    // errorFrequencyType() {
    //   switch (this.formData.errorFrequency) {
    //     case this.$t('complaintForm.ticket.engine.error_rate_1'): {
    //       return COMPLAINT_FORM_IMPULSE_ERROR_DESC_CONSTANTS.PERMANENT_ERROR_TYPE
    //     }
    //     case this.$t('complaintForm.ticket.engine.error_rate_2'): {
    //       return COMPLAINT_FORM_IMPULSE_ERROR_DESC_CONSTANTS.SPORADIC_ERROR_TYPE
    //     }
    //     default: {
    //       return this.formData.errorFrequency
    //     }
    //   }
    // },
    damage() {
      return this.damageTypes ? this.damageTypes.map(i => this.$t(i)) : null
    },
    errorFrequency() {
      return this.errorFrequencyType ? this.$t(this.errorFrequencyType) : null
    },
    damageDefaultLang() {
      return this.damageTypes
        ? this.damageTypes.map(i => this.$t(i, this.$i18n.fallbackLocale))
        : null
    },
    errorFrequencyDefaultLang() {
      return this.errorFrequencyType
        ? this.$t(this.errorFrequencyType, this.$i18n.fallbackLocale)
        : null
    },
    isPermanentErrorType() {
      return (
        this.errorFrequencyType === COMPLAINT_FORM_IMPULSE_ERROR_DESC_CONSTANTS.PERMANENT_ERROR_TYPE
      )
    },
    isSporadicErrorType() {
      return (
        this.errorFrequencyType === COMPLAINT_FORM_IMPULSE_ERROR_DESC_CONSTANTS.SPORADIC_ERROR_TYPE
      )
    },
    isContinentalType() {
      return (
        this.fullFormData?.modelType ===
        FORM_CONSTANTS.COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS.MODEL_TYPE
          .MODEL_TYPE_CONTINENTAL
      )
    },

    /** Select items */
    damageItems() {
      const items = [
        {
          text: `${this.$t('complaintForm.ticket.engine.damage_type3')} ${this.$t(
            'complaintForm.ticket.engine.damage_type3sub',
          )}`,
          value:
            COMPLAINT_FORM_IMPULSE_ERROR_DAMAGE_TYPES.COMPLAINT_FORM_TICKET_ENGINE_DAMAGE_TYPE3,
        },
        {
          text: `${this.$t('complaintForm.ticket.engine.damage_type4')} ${this.$t(
            'complaintForm.ticket.engine.damage_type4sub',
          )}`,
          value:
            COMPLAINT_FORM_IMPULSE_ERROR_DAMAGE_TYPES.COMPLAINT_FORM_TICKET_ENGINE_DAMAGE_TYPE4,
        },
      ]

      if (!this.isContinentalType) {
        items.unshift(
          {
            text: `${this.$t('complaintForm.ticket.engine.damage_type1')} ${this.$t(
              'complaintForm.ticket.engine.damage_type1sub',
            )}`,
            value:
              COMPLAINT_FORM_IMPULSE_ERROR_DAMAGE_TYPES.COMPLAINT_FORM_TICKET_ENGINE_DAMAGE_TYPE1,
          },
          {
            text: `${this.$t('complaintForm.ticket.engine.damage_type2')} ${this.$t(
              'complaintForm.ticket.engine.damage_type2sub',
            )}`,
            value:
              COMPLAINT_FORM_IMPULSE_ERROR_DAMAGE_TYPES.COMPLAINT_FORM_TICKET_ENGINE_DAMAGE_TYPE2,
          },
        )

        items.push(
          {
            text: `${this.$t('complaintForm.ticket.engine.damage_type5')} ${this.$t(
              'complaintForm.ticket.engine.damage_type5sub',
            )}`,
            value:
              COMPLAINT_FORM_IMPULSE_ERROR_DAMAGE_TYPES.COMPLAINT_FORM_TICKET_ENGINE_DAMAGE_TYPE5,
          },
          {
            text: `${this.$t('complaintForm.ticket.engine.damage_type6')} ${this.$t(
              'complaintForm.ticket.engine.damage_type6sub',
            )}`,
            value:
              COMPLAINT_FORM_IMPULSE_ERROR_DAMAGE_TYPES.COMPLAINT_FORM_TICKET_ENGINE_DAMAGE_TYPE6,
          },
        )
      }

      return items
    },
    errorFrequencyItems() {
      return [
        {
          text: `${this.$t('complaintForm.ticket.engine.error_rate_1')}`,
          value: COMPLAINT_FORM_IMPULSE_ERROR_DESC_CONSTANTS.PERMANENT_ERROR_TYPE,
        },
        {
          text: `${this.$t('complaintForm.ticket.engine.error_rate_2')}`,
          value: COMPLAINT_FORM_IMPULSE_ERROR_DESC_CONSTANTS.SPORADIC_ERROR_TYPE,
        },
      ]
    },

    /** Errors */
    damageTypesErrors() {
      return this.formErrorHelper('damageTypes', [this.chooseError])
      // return this.formErrorHelper('formData.damage', [this.chooseError])
    },
    descriptionErrors() {
      return this.formErrorHelper('formData.description', [this.requiredError])
    },
    errorFrequencyTypeErrors() {
      return this.formErrorHelper('errorFrequencyType', [this.chooseError])
    },
    distanceValueErrors() {
      return this.formErrorHelper('formData.distanceValue', [this.numericError])
    },
  },
  watch: {
    errorFrequency() {
      this.formData.errorFrequency = this.errorFrequency
      this.setCtdiDescriptions()
    },
    damage() {
      this.formData.damage = this.damage
      this.setCtdiDescriptions()
    },
  },
  mounted() {
    setLinksTargetForQuery('#cfed-software-note', '_blank')
  },
  methods: {
    ...mapMutations(FORM_CONSTANTS.STORE_ID, {
      updateFormData: FORM_MUTATION.UPDATE_FORM_DATA,
    }),
    submitCallback() {
      this.updateFormData(this.formData)
      this.nextStep()
    },
    setCtdiDescriptions() {
      this.formData.ctdiDescriptions = {
        ...this.ctdiDescriptions,
        [FORM_CONSTANTS.CTDI_DESCRIPTIONS_KEYS.ERROR_FREQUENCY]: this.errorFrequencyDefaultLang,
        [FORM_CONSTANTS.CTDI_DESCRIPTIONS_KEYS.DAMAGE]: this.damageDefaultLang,
      }
    },
  },
}
</script>
<style lang="scss" module>
.select {
  :global(.v-chip) {
    color: var(--white) !important;

    background-color: var(--primary) !important;

    &:global(.v-chip--select) {
      margin: 0.5rem 0.25rem !important;
    }
  }
}
</style>
